// theme/presets/cardSurface.ts — the card-colour contrast engine.
//
// THE GUARANTEE: the user picks HUE and CHROMA; the theme owns BRIGHTNESS. A picked
// colour is projected into a per-mode WCAG luminance band before it can ever reach a
// card, so there is no user input that produces sub-AA body or muted text.
//
// Two properties make this airtight:
//   1. The projection lives inside the pure `applyPreferences` seam, and surface /
//      surfaceRaised / glassBg are ALL derived from one resolved fill — so every
//      `color.surface` and `color.glassBg` consumer inherits the guarantee for free.
//      No component opts in.
//   2. The band is solved against the card AS RENDERED (fill + grain + sheen
//      composited), not the raw fill. This is mandatory: a dark card at the AA ceiling
//      with sheen + grain maxed composites ~0.010 higher and silently drops textMuted
//      below 4.5:1. Modelling only the raw fill ships that bug.
//
// ⚠ COUPLED TO MATERIAL_CAPS in cardMaterial.ts — the dark band is only ~0.0125 wide.

import {
  hexToRgb,
  mix,
  relativeLuminance,
  luminanceRgb,
  hexToHsl,
  hslToHex,
  contrastRatioL,
} from './color';
import { MATERIAL_CAPS } from './cardMaterial';

const NO_MATERIAL = { sheen: 0, grain: 0 };
const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

/**
 * The product band — a deliberate reserve slice inside the hard AA limit, leaving room
 * for `surfaceRaised` to step away from the card without leaving the legal range.
 */
const PRODUCT_BAND = {
  dark: { min: 0, max: 0.0125 },
  light: { min: 0.7, max: 1 },
} as const;

export interface Band {
  min: number;
  max: number;
  hardMin: number;
  hardMax: number;
  /** False when a degenerate custom accent had to be dropped from the guards. */
  accentEnforced: boolean;
}

/** Alpha-composite `src` over `dst` at alpha `a`. */
function over(src: readonly number[], a: number, dst: number[]): number[] {
  return dst.map((d, i) => (src[i] ?? 0) * a + d * (1 - a));
}

/** Accept either `#rrggbb` or an opaque `rgb(r, g, b)` string (what `flatten` emits). */
function parseColor(c: string): { r: number; g: number; b: number } {
  const m = c.match(/rgba?\(([^)]+)\)/i);
  if (m && m[1]) {
    const p = m[1].split(',').map((s) => parseFloat(s.trim()));
    return { r: p[0] ?? 0, g: p[1] ?? 0, b: p[2] ?? 0 };
  }
  return hexToRgb(c);
}

/**
 * Luminance of the card AS RENDERED — fill with grain then sheen composited.
 * Sheen is evaluated at its PEAK alpha (y = 0 of the band), the conservative reading:
 * it is exactly where PostCard paints the author name and handle.
 */
export function effectiveLuminance(
  fill: string,
  mode: 'dark' | 'light',
  m: { sheen: number; grain: number },
): number {
  const c = MATERIAL_CAPS[mode];
  const f = parseColor(fill);
  let v: number[] = [f.r, f.g, f.b];
  if (m.grain > 0) v = over([128, 128, 128], c.grain * m.grain, v); // noise mean grey
  if (m.sheen > 0) v = over(c.sheenSrc, c.sheen * m.sheen, v);
  return luminanceRgb(v[0] ?? 0, v[1] ?? 0, v[2] ?? 0);
}

/**
 * Bisect HSL LIGHTNESS so the RENDERED card lands at `target`. Hue + saturation are
 * preserved exactly. HSL l=0 is black and l=1 is white for every (h,s), so a solution
 * ALWAYS exists — this can never fail or throw.
 */
function solveFill(
  seed: string,
  mode: 'dark' | 'light',
  m: { sheen: number; grain: number },
  target: number,
): string {
  const { h, s } = hexToHsl(seed);
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    if (effectiveLuminance(hslToHex(h, s, mid), mode, m) < target) lo = mid;
    else hi = mid;
  }
  return hslToHex(h, s, (lo + hi) / 2);
}

/**
 * Derive the legal luminance band from the LIVE text/accent tokens (not hard-coded), so
 * a future token change can never silently invalidate it.
 *
 * Guards: textPrimary 4.5, textMuted 4.5 (body text), accent 3.0 (WCAG non-text/icon).
 * textMuted binds in both modes. A degenerate custom accent (e.g. near-black) would
 * empty the band, so the accent guard is applied only while the band stays ≥ 0.002 wide;
 * otherwise it is dropped and `accentEnforced` reports false. Text safety is structural;
 * accent safety is best-effort-but-enforced-whenever-enforceable.
 */
export function solveBand(
  mode: 'dark' | 'light',
  textPrimary: string,
  textMuted: string,
  accent: string,
): Band {
  const lP = relativeLuminance(textPrimary);
  const lM = relativeLuminance(textMuted);
  const lA = relativeLuminance(accent);

  if (mode === 'dark') {
    // Card must sit BELOW the text: L ≤ (L_text + 0.05) / ratio − 0.05
    const capFor = (l: number, ratio: number): number => (l + 0.05) / ratio - 0.05;
    const textCap = Math.min(capFor(lP, 4.5), capFor(lM, 4.5));
    const combined = Math.min(textCap, capFor(lA, 3));
    const accentEnforced = combined >= 0.002;
    const hardMax = accentEnforced ? combined : textCap;
    return {
      min: PRODUCT_BAND.dark.min,
      max: Math.min(PRODUCT_BAND.dark.max, hardMax),
      hardMin: 0,
      hardMax,
      accentEnforced,
    };
  }

  // Card must sit ABOVE the text: L ≥ ratio × (L_text + 0.05) − 0.05
  const floorFor = (l: number, ratio: number): number => ratio * (l + 0.05) - 0.05;
  const textFloor = Math.max(floorFor(lP, 4.5), floorFor(lM, 4.5));
  const combined = Math.max(textFloor, floorFor(lA, 3));
  const accentEnforced = 1 - combined >= 0.002;
  const hardMin = accentEnforced ? combined : textFloor;
  return {
    min: Math.max(PRODUCT_BAND.light.min, hardMin),
    max: PRODUCT_BAND.light.max,
    hardMin,
    hardMax: 1,
    accentEnforced,
  };
}

/**
 * Resolve the card fill: mix the base surface toward the user's seed by `strength`, then
 * project the RENDERED result into the band. An in-band result is returned UNTOUCHED, so
 * the shipped default (strength 0) is byte-identical to `base.color.surface`.
 */
export function resolveCardSurface(
  mode: 'dark' | 'light',
  baseSurface: string,
  cardColor: string,
  strength: number,
  m: { sheen: number; grain: number },
  band: Band,
): string {
  const s = clamp01(strength);
  const seed = s <= 0 || !HEX_RE.test(cardColor) ? baseSurface : mix(baseSurface, cardColor, s);
  const L = effectiveLuminance(seed, mode, m);
  if (L >= band.min - 1e-9 && L <= band.max + 1e-9) return seed;
  return solveFill(seed, mode, m, Math.min(band.max, Math.max(band.min, L)));
}

/**
 * `surfaceRaised` (popovers, menus, raised chrome) paints NO material layers, so it is
 * solved against the bare fill — stepped away from the card just enough to read as raised.
 */
export function resolveCardRaised(mode: 'dark' | 'light', fill: string, band: Band): string {
  const L = relativeLuminance(fill);
  const target =
    mode === 'dark'
      ? Math.min(band.hardMax, L + 0.006)
      : L >= 0.94
        ? L - 0.06
        : Math.min(1, L + 0.06);
  return solveFill(fill, mode, NO_MATERIAL, target);
}

/**
 * "Never worse than today" guard — the ONLY text token this design moves.
 *
 * `textFaint` is already sub-AA on the shipped surface (it is decorative: timestamps,
 * separators), so binding the band to it would collapse the band. Instead we hold it to
 * `min(ideal, the ratio it already has on the BASE surface)`: an exact no-op at defaults,
 * and on a customised card it can only ever IMPROVE. Safe to move globally because we only
 * ever push it AWAY from the surface extreme, and bgRoot is never further in that
 * direction than the furthest allowed card — so off-card contrast can only improve.
 */
export function ensureNoWorse(
  token: string,
  cardL: number,
  baseSurface: string,
  ideal: number,
  mode: 'dark' | 'light',
): string {
  const lTok = relativeLuminance(token);
  const target = Math.min(ideal, contrastRatioL(lTok, relativeLuminance(baseSurface)));
  if (contrastRatioL(lTok, cardL) >= target) return token;
  const anchor = mode === 'dark' ? '#FFFFFF' : '#000000';
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 22; i++) {
    const mid = (lo + hi) / 2;
    if (contrastRatioL(relativeLuminance(mix(token, anchor, mid)), cardL) >= target) hi = mid;
    else lo = mid;
  }
  return mix(token, anchor, hi);
}
